import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";

import { FileTypeIcon } from "../../../components/common/FileTypeIcon";
import {
  CitationPdfViewer,
  type CitationPageOverlayRenderArgs
} from "../../../components/common/citations/CitationPdfViewer";
import {
  citationHighlightRect,
  citationPageOffsetRatio,
  renderedPageHeightFor,
  type CitationGeometry
} from "../../../components/common/citations/citationHighlight";

type Props = {
  file: string;
  /**
   * Where the value sits, when a suggestion opened this. Absent when the client
   * asked to see a document rather than to check one answer in it, and then
   * there is nothing to highlight and nowhere in particular to open.
   */
  geometry?: CitationGeometry | null;
  /** 1-based, as Extend reports it. */
  page?: number | null;
  filename?: string | null;
  /** False while closing, so the panel can animate out before it unmounts. */
  isOpen: boolean;
  onClose: () => void;
};

/**
 * The whole cited page, still highlighted, docked beside the chat.
 *
 * On a wide screen this sits immediately left of Casey rather than over the top
 * of everything, matching the preview the intake opens beside its uploaded-files
 * rail. That is the point of it: the client can read the document, the answer
 * Casey suggested, and the question it belongs to at the same time, which is
 * exactly the comparison a modal takes away by covering the other two.
 *
 * Narrow screens have no room to put anything beside a panel that is already the
 * full width, so there it stays an overlay above the chat.
 *
 * Portalled because the chat clips its own overflow and, being transformed, would
 * otherwise anchor this to itself rather than to the viewport.
 */
export function CitationViewerPanel({
  file,
  geometry,
  page,
  filename,
  isOpen,
  onClose
}: Props) {
  // Mounted hidden and shown on the next frame, so the browser has a "from"
  // state to animate out of. The same flag runs in reverse on the way out.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Stopped so the chat's own Escape handler does not close Casey behind
      // this, which would leave the client somewhere they did not ask to be.
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  const renderOverlay = useCallback(
    ({ pageNumber, renderedPageWidth }: CitationPageOverlayRenderArgs) => {
      if (!geometry || pageNumber !== page) return null;

      const renderedPageHeight = renderedPageHeightFor(
        geometry,
        renderedPageWidth
      );
      if (renderedPageHeight === null) return null;

      const rect = citationHighlightRect(geometry, {
        renderedPageWidth,
        renderedPageHeight
      });
      if (rect === null) return null;

      return (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute rounded-xs bg-warn/30 ring-1 ring-warn/70"
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
          }}
        />
      );
    },
    [geometry, page]
  );

  if (typeof document === "undefined") return null;

  const shown = entered && isOpen;

  return createPortal(
    <div
      className={[
        // Below lg: an overlay over the chat, which is a full-width sheet there.
        "fixed inset-0 z-1600 flex flex-col bg-black/40 p-3",
        // lg and up: docked in the gap left of Casey. The offsets track the panel
        // in CaseyPanel.tsx -- `right-6` plus its `w-105`, plus a gap -- and
        // the width leaves the same margin on the far side that Casey has on its
        // own. The height matches Casey's so the two read as one surface.
        "lg:inset-auto lg:bottom-6 lg:right-114 lg:z-1490 lg:bg-transparent lg:p-0",
        "lg:h-[min(680px,calc(100vh-96px))] lg:w-[min(600px,calc(100vw-480px))]",
        "transition-[opacity,transform] duration-200 ease-out",
        shown ? "opacity-100 lg:translate-x-0" : "opacity-0 lg:translate-x-4"
      ].join(" ")}
    >
      {/* Tapping away closes it on a narrow screen. Docked there is nothing to
          tap away from: the client is meant to keep using the form behind it. */}
      <button
        type="button"
        aria-label="Close document"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default lg:hidden"
      />
      <div
        role="dialog"
        aria-label={filename || "Document"}
        className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-2"
      >
        <header className="flex h-11 shrink-0 items-center justify-between gap-3 border-b border-line-2 pr-2 pl-4">
          <span className="flex min-w-0 items-center gap-2 text-md font-medium text-fg">
            {/* The same mark the chat and the intake's own rail put beside this
                file, so the client can see it is the one they opened. */}
            <FileTypeIcon name={filename || ""} size={15} />
            <span className="truncate">{filename || "Your document"}</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Close"
            onClick={onClose}
          >
            <X />
          </Button>
        </header>
        <div className="min-h-0 flex-1">
          <CitationPdfViewer
            file={file}
            ariaLabel={`${filename || "Document"} preview`}
            scrollToPageIndex={page ? Math.max(0, page - 1) : 0}
            scrollToPageOffsetRatio={
              geometry ? citationPageOffsetRatio(geometry) : null
            }
            renderPageOverlay={renderOverlay}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
